<script setup lang="ts">
import { LazyConfirmModal } from '#components'
import {
  SUGGESTION_COMMENT_MAX,
  canModerateSuggestions,
  type SuggestionComment
} from '~~/shared/utils/suggestions'

interface Props {
  suggestionId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  /** Total comment count after a post or delete. */
  count: [total: number]
}>()

const authStore = useAuthStore()
const user = useSupabaseUser()
const route = useRoute()
const toast = useToast()
const overlay = useOverlay()
const confirmModal = overlay.create(LazyConfirmModal)

const { data } = await useFetch<{ comments: SuggestionComment[], total: number }>(
  `/api/suggestions/${props.suggestionId}/comments`,
  { key: `suggestion-comments-${props.suggestionId}` }
)

// Oldest first. "Load more" appends later pages; a comment posted here is
// appended right away, so later pages are de-duplicated by id.
const comments = ref<SuggestionComment[]>(data.value?.comments ?? [])
const total = ref(data.value?.total ?? 0)
const page = ref(1)
const loadingMore = ref(false)
const hasMore = computed(() => comments.value.length < total.value)

const isLoggedIn = computed(() => !!user.value?.sub)
// Characters load client-side; only block the composer once we know there is no username
const needsUsername = computed(() => !!authStore.character && !authStore.character.username)

const draft = ref('')
const posting = ref(false)

async function loadMore() {
  loadingMore.value = true
  try {
    const next = await $fetch<{ comments: SuggestionComment[], total: number }>(
      `/api/suggestions/${props.suggestionId}/comments`,
      { query: { page: page.value + 1 } }
    )
    const known = new Set(comments.value.map(c => c.id))
    comments.value.push(...next.comments.filter(c => !known.has(c.id)))
    total.value = next.total
    page.value++
  } catch {
    toast.add({ title: 'Could not load comments', color: 'error', icon: 'i-lucide-alert-circle' })
  } finally {
    loadingMore.value = false
  }
}

async function postComment() {
  const content = draft.value.trim()
  if (!content || posting.value) return

  posting.value = true
  try {
    const { comment } = await $fetch<{ comment: SuggestionComment }>(
      `/api/suggestions/${props.suggestionId}/comments`,
      { method: 'POST', body: { content } }
    )
    comments.value.push(comment)
    total.value++
    emit('count', total.value)
    draft.value = ''
  } catch (err) {
    toast.add({
      title: 'Comment not posted',
      description: fetchErrorMessage(err),
      color: 'error',
      icon: 'i-lucide-alert-circle'
    })
  } finally {
    posting.value = false
  }
}

function canDelete(comment: SuggestionComment) {
  return canModerateSuggestions(authStore.role) || (!!authStore.userId && authStore.userId === comment.author.id)
}

async function deleteComment(comment: SuggestionComment) {
  const confirmed = await confirmModal.open({
    title: 'Delete comment?',
    description: 'This cannot be undone.',
    confirmText: 'Delete'
  }).result
  if (!confirmed) return

  try {
    await $fetch(`/api/suggestions/${props.suggestionId}/comments/${comment.id}`, { method: 'DELETE' })
    comments.value = comments.value.filter(c => c.id !== comment.id)
    total.value = Math.max(0, total.value - 1)
    emit('count', total.value)
  } catch (err) {
    toast.add({
      title: 'Could not delete comment',
      description: fetchErrorMessage(err),
      color: 'error',
      icon: 'i-lucide-alert-circle'
    })
  }
}

function authorName(comment: SuggestionComment) {
  return comment.author.display_name || comment.author.username || 'Deleted user'
}
</script>

<template>
  <section aria-labelledby="suggestion-comments-heading">
    <h2
      id="suggestion-comments-heading"
      class="mb-4 text-lg font-semibold text-highlighted"
    >
      Comments
      <span class="text-muted font-normal">({{ total }})</span>
    </h2>

    <ul
      v-if="comments.length"
      class="space-y-3"
    >
      <li
        v-for="comment in comments"
        :key="comment.id"
        class="flex gap-3 rounded-xl border border-default bg-elevated/40 p-4"
      >
        <UAvatar
          :src="comment.author.avatar_url || undefined"
          :alt="authorName(comment)"
          size="sm"
        />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <NuxtLink
              v-if="comment.author.username"
              :to="`/${comment.author.username}`"
              class="font-medium text-highlighted hover:underline"
            >
              {{ authorName(comment) }}
            </NuxtLink>
            <span
              v-else
              class="font-medium text-muted"
            >
              {{ authorName(comment) }}
            </span>
            <UBadge
              v-if="comment.is_staff"
              label="Team"
              icon="i-lucide-badge-check"
              color="primary"
              variant="subtle"
              size="sm"
            />
            <NuxtTime
              :datetime="comment.created_at"
              relative
              locale="en-US"
              class="text-xs text-dimmed"
            />
            <UButton
              v-if="canDelete(comment)"
              icon="i-lucide-trash-2"
              color="neutral"
              variant="ghost"
              size="xs"
              class="ml-auto"
              aria-label="Delete comment"
              @click="deleteComment(comment)"
            />
          </div>
          <p class="mt-1 whitespace-pre-line wrap-break-word text-sm text-toned">
            {{ comment.content }}
          </p>
        </div>
      </li>
    </ul>

    <p
      v-else
      class="rounded-xl border border-dashed border-default p-6 text-center text-sm text-muted"
    >
      No comments yet.
    </p>

    <div
      v-if="hasMore"
      class="mt-3 flex justify-center"
    >
      <UButton
        label="Load more comments"
        color="neutral"
        variant="outline"
        size="sm"
        :loading="loadingMore"
        @click="loadMore"
      />
    </div>

    <!-- Composer -->
    <div class="mt-6">
      <div
        v-if="!isLoggedIn"
        class="flex flex-col items-start gap-3 rounded-xl border border-default bg-elevated/40 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <p class="text-sm text-muted">
          Sign in to join the discussion.
        </p>
        <UButton
          label="Sign in"
          color="neutral"
          variant="outline"
          size="sm"
          :to="loginPathFor(route.fullPath)"
        />
      </div>

      <div
        v-else-if="needsUsername"
        class="flex flex-col items-start gap-3 rounded-xl border border-default bg-elevated/40 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <p class="text-sm text-muted">
          Pick a username to comment.
        </p>
        <UButton
          label="Pick a username"
          color="neutral"
          variant="outline"
          size="sm"
          to="/create-username"
        />
      </div>

      <form
        v-else
        class="space-y-2"
        @submit.prevent="postComment"
      >
        <UTextarea
          v-model="draft"
          placeholder="Add a comment"
          :rows="3"
          :maxlength="SUGGESTION_COMMENT_MAX"
          autoresize
          class="w-full"
          aria-label="Comment"
          @keydown.meta.enter.prevent="postComment"
          @keydown.ctrl.enter.prevent="postComment"
        />
        <div class="flex items-center justify-between gap-3">
          <span class="text-xs text-dimmed">{{ draft.length }}/{{ SUGGESTION_COMMENT_MAX }}</span>
          <UButton
            type="submit"
            label="Comment"
            color="primary"
            size="sm"
            :loading="posting"
            :disabled="!draft.trim()"
          />
        </div>
      </form>
    </div>
  </section>
</template>
